/**
 * Switch and checkbox widget.
 *
 * Takes 3 options: label,on,off.  All of these expect localizations keys.
 * Or: onLabel,offLabel, and staticLabel take static strings.
 *
 * onValue: The value for widget to have when switch is on
 * offValue: The value for widget to have when switch is off
 *
 * label: The label of the item.  Optional.
 * off,on: Labels for false,true state.  Must be both provided or neither.  Including them causes a switch to render.  Excluding them causes a checkbox
 */
import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import classes from "app/css/widgets/DFSwitch.module.less";

const DFSwitch = BaseWidget(kendo.ui.Widget).extend({

    plain: '<label class="checkSwitch"><input type="checkbox"> <span data-label></span><br></label>',

    noLabel: '<span class="checkSwitch"><input type="checkbox"><br></span>',

    switch: '<label class="switch-light no-label switch-candy notChecked"><input type="checkbox">' +
        '<span><span data-off-label></span><span data-on-label></span></span><a></a></label>',

    options: {
        name: "DFSwitch",
        label: null, //locale key for label
        staticLabel: null, //string for label
        on: null, //locale key for on label
        off: null, //locale key for off label
        onLabel: null, //string for on label
        offLabel: null, //string for off label
        onValue: true, //Custom value for input/output
        offValue: false //Custom value for input/output
    },

    events: ["change"],

    init: function(element, options) {

        kendo.ui.Widget.fn.init.call(this, element, options);

        var me = this;

        this.model = kendo.observable({
            value: false,
            enabled: true,
            readonly: false,
            visible: true,
            tab: ""
        });

        this.id = this.element.attr("id");
        if (this.id == undefined) {
            console.error("Switch widget requires an id");
        }

        this.element.wrap($("<div>"));
        this.wrapper = this.element.parent();
        this.wrapper.addClass(classes.DFSwitch);

        var label = (options.label !== undefined) ? locale.get(options.label) : (options.staticLabel != undefined ? options.staticLabel : "");
        var on = (options.onLabel != undefined ? options.onLabel : (options.on !== undefined) ? locale.get(options.on) : "");
        var off = (options.offLabel != undefined ? options.offLabel : (options.off !== undefined) ? locale.get(options.off) : "");


        if ((options.on && options.off) || (options.onLabel && options.offLabel)) {
            this.widget = $(this.switch);
            this.usePlain = false;
            this.widget.find("[data-on-label]").text(on);
            this.widget.find("[data-off-label]").text(off);
        } else {
            this.widget = $(label ? this.plain : this.noLabel);
            this.usePlain = true;
            this.widget.find("[data-label]").text(label);
        }

        var input = this.widget.find("input");
        input.attr("id", this.id + "_check");
        input.attr("tabindex", this.model.get("tab"));
        input.attr("aria-describedby", this.element.attr("aria-describedby"));
        input.attr("aria-label", this.element.attr("aria-label"));
        input.attr("aria-labelledby", this.element.attr("aria-labelledby"));
        if (!me.usePlain) {
            me.widget.on("click", function(e) {
                e.stopPropagation();
            });
        }
        input.on("change", function() {
            me.model.set("value", input[0].checked);
        });

        //Readonly isn't supported automatically on checkboxes.  This simulates it
        this.widget.bind("click", function(e) {
            if (me.model.get("readonly") == true) {
                e.preventDefault();
            }
        });
        this.widget.bind("keydown", function(e) {
            if (me.model.get("readonly") == true && e.keyCode == 32) {
                e.preventDefault();
            }
        });

        this.model.bind("change", function(e) {

            //Control data
            if (e.field == "value") {
                me.trigger("change");
                var v = this.get("value");
                me.widget.toggleClass("checked", v).toggleClass("notChecked", !v);
            }
            if (e.field == "value" || e.field == "readonly") {
                this.set("visible", this.get("value") || !this.get("readonly"));
            }
            if (e.field == "readonly") {
                this.set("tab", this.get("readonly") ? "-1" : "");
            }

            //Adjust UI
            if (e.field == "visible" && me.usePlain) {
                if (this.get(e.field)) {
                    me.widget.show();
                } else {
                    me.widget.hide();
                }
            } else if (e.field == "tab") {
                input.attr("tabindex", this.get("tab"));
            } else if (e.field == "readonly" && me.usePlain) {
                if (this.get(e.field)) {
                    input.hide();
                } else {
                    input.show();
                }
            } else if (e.field == "enabled") {
                if (this.get(e.field)) {
                    input.removeAttr("disabled");
                } else {
                    input.attr("disabled", true);
                }
            } else if (e.field == "value") {
                input[0].checked = this.get("value");
            }
        });

        // k-state-focused class needed for styling of validation message on selection
        this.wrapper.on("focusin.dfswitch", function() {
            $(this)
                .addClass(classes.focused)
                .addClass("k-state-focused");
        });
        this.wrapper.on("focusout.dfswitch", function() {
            $(this)
                .removeClass(classes.focused)
                .removeClass("k-state-focused");
        });

        this.widget.insertAfter(this.element);
        this.element.hide();
        this.wrapper.addClass(this.element.attr("class")); //Copy over classes to make styling easier

    },

    getARIAElements: function() {
        return this.widget.find("input");
    },

    /**
     * Setter/getter for enabled state
     * @param enable
     */
    enable: function(enable) {
        if (enable == false) {
            this.widget.addClass("disabled");
            this.model.set("enabled", false);
        } else {
            this.widget.removeClass("disabled");
            this.model.set("enabled", true);
        }
    },

    /**
     * Setter/getter for readonly state
     * @param val
     * @returns {*}
     */
    readonly: function(val) {
        if (val == undefined) {
            return this.model.get("readonly");
        }

        if (val) {
            this.widget.addClass("readOnly");
            val = true;
        } else {
            this.widget.removeClass("readOnly");
            val = false;
        }

        this.model.set("readonly", val);
    },

    /**
     * Set or get the value of the widget.  Returns either onValue(default=true) or offValue(default=false).
     * On setter, the strings "true"/"false" are converted to onValue/offValue.
     * @param val value to set
     * @returns {boolean} current state
     */
    value: function(val) {
        if (val === "true") {
            val = this.options.onValue;
        } else if (val === "false") {
            val = this.options.offValue;
        }

        if (val === this.options.onValue) {
            this.model.set("value", true);
        } else if (val === this.options.offValue) {
            this.model.set("value", false);
        }

        return this.model.get("value") ? this.options.onValue : this.options.offValue;
    },

    /**
     * Focus the widget.
     */
    focus: function() {
        this.getARIAElements().focus();
    },

    /**
     * Cleanup the widget
     */
    destroy: function() {
        if(this.wrapper){
            this.wrapper.off("focusin.dfswitch");
            this.wrapper.off("focusout.dfswitch");
        }
        this.element.unwrap(`div.${classes.DFSwitch}`);
        this.widget.remove();
        this.element.show();

        kendo.ui.Widget.fn.destroy.apply(this, arguments);
        delete this.wrapper;
    }

});

kendo.ui.plugin(DFSwitch);
export default DFSwitch;
